import { Component, OnInit, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import { Company } from 'src/app/models/company.model';
import { User } from 'src/app/models/user.model';
import { GeneralSearchDialogComponent } from 'src/app/components/utils/general-search-dialog/general-search-dialog.component';
import { AddNewWorkingTimeDialogComponent } from '../activity/working-times-history/add-new-working-time-dialog/add-new-working-time-dialog.component';
import { WorkingTime } from 'src/app/models/working_time.model';
import { NewLearningResourceRequestDialogComponent } from './new-learning-resource-request-dialog/new-learning-resource-request-dialog.component';
import {
  AuthService,
  ClockRegisterService,
  CustomerService,
  ApiService,
  UserService,
  EmployeeService,
  UtilsService,
  PermissionsService,
} from 'src/app/services';
import { PlatformService } from 'src/app/services/platform.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { RegisterBeforeLeaveDialogComponent } from '../main-dialogs/register-before-leave-dialog/register-before-leave-dialog.component';
import {
  ClockOutScheduleWarningDialogComponent,
  ClockOutScheduleWarningDialogData,
  ClockOutScheduleWarningDialogResult,
} from '../main-dialogs/clock-out-schedule-warning-dialog/clock-out-schedule-warning-dialog.component';
import {
  ClockInScheduleWarningDialogComponent,
  ClockInScheduleWarningDialogData,
  ClockInScheduleWarningDialogResult,
} from '../main-dialogs/clock-in-schedule-warning-dialog/clock-in-schedule-warning-dialog.component';
import { ConfigureWorkingScheduleDialogComponent } from '../activity/working-times-calendar/configure-working-schedule-dialog/configure-working-schedule-dialog.component';
import { ConfigureWorkingScheduleDialogData } from 'src/app/models/working_contract.model';
import { ScheduleMode } from 'src/app/enums/schedule-mode.enum';
import { UpdateAvailableDialogComponent } from '../../shared/update-available-dialog/update-available-dialog.component';
import { InputDialogComponent } from '../../shared/input-dialog/input-dialog.component';
import { ConfigureBisualMcpDialogComponent } from './configure-bisual-mcp-dialog/configure-bisual-mcp-dialog.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { SecondsToStringTimePipe } from 'src/app/pipes/seconds-to-string-time.pipe';
import { RecordingStatusBarComponent } from './recording-status-bar/recording-status-bar.component';
import { NavigationHistoryComponent } from './navigation-history/navigation-history.component';
import { WorkingTimeSummary } from 'src/app/models/working-time-summary.model';
import { EmployeeRequestResponsibleForAnswerStatusEnum } from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { WorkingExtraHoursPeriod } from 'src/app/models/working-extra-hours-period.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { RemoteWorkStatus } from 'src/app/models/remote-work-status';

@Component({
  selector: 'app-top-menu-employee',
  templateUrl: './top-menu-employee.component.html',
  styleUrls: ['./top-menu-employee.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    MatIconButton,
    MatIcon,
    NgClass,
    MatButton,
    MatTooltip,
    MatProgressSpinner,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
    DatePipe,
    SecondsToStringTimePipe,
    MatDividerModule,
    RecordingStatusBarComponent,
    NavigationHistoryComponent,
  ],
})
export class TopMenuEmployeeComponent implements OnInit {
  clientSelectControl = new FormControl();
  customerSelected: Company | null = null;
  available_customers: Company[] = [];
  showHeaderMenu: boolean = false;
  me: User;
  role_string: string | undefined;

  // Signals for reactive state
  isAbsent = signal(false);
  hasWorkingHoursToday = signal(true);
  hasApprovedExtraHours = signal(false);
  isAtOffice = signal(true); // Assume at office by default
  can_work_remotely = signal(false);

  canClockIn = computed((): boolean => {
    // Check absence
    if (this.isAbsent() && !this.hasApprovedExtraHours()) {
      return false;
    }

    // Check working hours or extra hours
    const hasHours = this.hasWorkingHoursToday() || this.hasApprovedExtraHours();
    if (!hasHours) return false;

    // Check remote work if not at office
    if (!this.isAtOffice()) {
      return this.can_work_remotely();
    }

    return true;
  });

  // Computed signal for tooltip message
  clockButtonTooltip = computed(() => {
    if (this.canClockIn()) {
      return '';
    }

    if (this.isAbsent() && !this.hasApprovedExtraHours()) {
      return 'No puedes fichar porque tienes una ausencia registrada para hoy';
    }

    if (!this.hasWorkingHoursToday() && !this.hasApprovedExtraHours()) {
      return 'No puedes fichar porque hoy no tienes horario laboral asignado ni horas extra aprobadas';
    }

    if (!this.isAtOffice() && !this.can_work_remotely()) {
      return 'No puedes fichar porque no estás en la oficina, no tienes teletrabajo permitido ni viaje registrado para hoy. Si estás en la oficina, conéctate al WiFi y haz CMD + R.';
    }

    return 'No puedes fichar en este momento';
  });

  scheduleButtonDisabled(): boolean {
    if (!this.canClockIn()) {
      return true;
    }

    return this.clock_schedule_status && this.rest_status_last_in === true;
  }

  canClickClock(): boolean {
    return !this.scheduleButtonDisabled() && !this.isClockActionInProgress();
  }

  scheduleButtonTooltip(): string {
    if (!this.canClockIn()) {
      return this.clockButtonTooltip();
    }

    if (this.clock_schedule_status && this.rest_status_last_in === true) {
      return 'No puedes registrar salida mientras el descanso está iniciado. Finaliza el descanso primero.';
    }

    return '';
  }

  nextTaskTitle: string | null = null;
  nextStartDate: Date | null = null;
  currentTaskTitle: string | null = null;
  currentTaskCallLink: string | null = null;
  show_live_task_indicator = false;

  // Clock Schedule Status
  clock_schedule_status: boolean = false;

  // Clock Rest Status
  clock_rest_available: boolean = false;
  clock_rest_total: number | null = null;
  clock_rest_consumed: number | null = null;
  rest_status_last_in: boolean | null = null;
  rest_status_last_time: Date | null = null;
  colorRestSpinner: string = 'accent';

  parentUser: User | null = null;

  // Work time summary
  workingTimeSummaryList: WorkingTimeSummaryItem[] = [];
  setSummary: WorkingTimeSummaryItem;
  currentSetShowing: number;
  private last_working_time_summary_user_id: number | null = null;

  protected employee = inject(EmployeeService);
  protected platform = inject(PlatformService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private clock = inject(ClockRegisterService);
  protected readonly isClockActionInProgress = toSignal(this.clock.clockActionInProgress$, { initialValue: false });
  private customerService = inject(CustomerService);
  private api = inject(ApiService);
  private userService = inject(UserService);
  private utils = inject(UtilsService);
  private snack = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  protected permissions = inject(PermissionsService);

  ngOnInit(): void {
    this.checkStartAttribute();
    this.listenCustomerSelected();
    this.listenControlSearch();
    this.subscribeCurrentTask();
    this.listenClockScheduleStatus();
    this.listenClockRestStatus();
    this._listenUser();
    this.employee.getUpdateHoursTopMenu().subscribe(() => {
      this.fetchWorkingTimeSummary();
    });
  }

  clickClock(): void {
    if (!this.canClickClock()) {
      return;
    }

    this.performClock();
  }

  private performClock(body: Record<string, unknown> | null = null): void {
    this.clock.click(body)?.subscribe(status => {
      this.handleClick(status);
    });
  }

  private openLateExitDialog(data: ClockOutScheduleWarningDialogData): void {
    const dialogRef = this.dialog.open(ClockOutScheduleWarningDialogComponent, {
      width: '560px',
      data,
    });

    dialogRef.afterClosed().subscribe((result: ClockOutScheduleWarningDialogResult | undefined): void => {
      if (result === 'clock-out') {
        this.performClock({ confirm_schedule_exit: true });
      } else if (result === 'manage-exception') {
        this.openWeekExceptionScheduleDialog();
      }
    });
  }

  private openEarlyEntryDialog(data: ClockInScheduleWarningDialogData): void {
    const dialogRef = this.dialog.open(ClockInScheduleWarningDialogComponent, {
      width: '560px',
      data,
    });

    dialogRef.afterClosed().subscribe((result: ClockInScheduleWarningDialogResult | undefined): void => {
      if (result === 'clock-in') {
        this.performClock({ confirm_schedule_entry: true });
      } else if (result === 'manage-exception') {
        this.openWeekExceptionScheduleDialog();
      }
    });
  }

  /** Abre el diálogo de excepción horaria de la semana actual para que el usuario ajuste las franjas. */
  private openWeekExceptionScheduleDialog(): void {
    const user_id = this.userService.getCurrentUser().id;

    this.api.getActiveWorkingContractScheduleForUser(user_id).subscribe(schedule => {
      const from = dayjs().startOf('isoWeek');
      const to = dayjs().endOf('isoWeek');
      const data: ConfigureWorkingScheduleDialogData = {
        working_contract: this.userService.getCurrentUser().current_working_contract,
        weekly_schedule: schedule.working_contract_weekly_schedule,
        weekly_schedule_exception: schedule.working_contract_weekly_schedule_exception ?? undefined,
        schedule_mode: ScheduleMode.WeekException,
        start_week_date: from.format('YYYY-MM-DD'),
        end_week_date: to.format('YYYY-MM-DD'),
        dialog_title: 'Configurar horario de esta semana',
        dialog_subtitle: `Semana del ${from.format('DD/MM/YYYY')} al ${to.format('DD/MM/YYYY')}`,
        allowed_remote_work_days_resolved: schedule.allowed_remote_work_days_resolved,
      };

      this.dialog.open(ConfigureWorkingScheduleDialogComponent, {
        width: '1200px',
        maxWidth: '95vw',
        data,
        autoFocus: false,
      });
    });
  }

  clickClockRest() {
    this.clock.clickRest()?.subscribe({
      next: response => {
        if (response.flag === 'no-register') {
          this.dialog.open(RegisterBeforeLeaveDialogComponent, { width: '500px' });
        }
        if (response.flag === 'clock-busy') {
          this.snack.open('Ya hay un fichaje en curso, espera un momento.', 'Ok', { duration: 4000 });
        }
      },
    });
  }

  selectCustomer(c: Company) {
    this.customerService.selectCustomer(c);
  }

  resetCustomer() {
    this.available_customers = [];
    this.clientSelectControl.setValue('');
    this.customerService.reset();
  }

  displayFn(company: Company): string {
    return company ? company.fact_name : '';
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/auth', 'login']);
  }

  valueRestTime() {
    if (this.rest_status_last_time == null || this.clock_rest_total == null || this.clock_rest_consumed == null) return 0;

    const availableSecondsSinceLastApiCall: number = this.clock_rest_consumed;
    let value = availableSecondsSinceLastApiCall;
    if (this.rest_status_last_in) {
      value -= dayjs(this.rest_status_last_time).diff(dayjs(), 'seconds');
    }

    if (value < 0) {
      value = 100 - value;
      this.colorRestSpinner = 'warn';
    } else this.colorRestSpinner = 'accent';

    return (value / this.clock_rest_total) * 100;
  }

  openGeneralSearch() {
    this.dialog.open(GeneralSearchDialogComponent, {
      width: '650px',
      data: {},
      panelClass: 'custom-dialog-container',
    });
  }

  openBisualBudgetRequest() {
    this.api.getBisualSetting('bisual-budget-request-url').subscribe(data => {
      window.open(data.value, '_blank');
    });
  }

  openConfigureBisualMcpDialog(): void {
    this.dialog.open(ConfigureBisualMcpDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  requestPostgresBisualDatabaseGrant(): void {
    const dialog_ref = this.dialog.open(InputDialogComponent, {
      width: '480px',
      data: {
        title: 'Acceso de escritura a PostgreSQL',
        description: 'Indica el motivo (mínimo 20 caracteres). El acceso se habilitará durante 1 hora.',
        type: 'text',
        placeholder: 'Motivo del acceso...',
        label: 'Motivo',
        close_button_text: 'Habilitar 1 hora',
      },
    });

    dialog_ref.afterClosed().subscribe((reason: string | undefined) => {
      if (reason == null) {
        return;
      }

      const trimmed_reason = reason.trim();
      if (trimmed_reason.length < 20) {
        this.snack.open('El motivo debe tener al menos 20 caracteres.', 'Ok', { duration: 4000 });
        return;
      }

      this.api.createPostgresBisualDatabaseGrantRequest({ reason: trimmed_reason }).subscribe({
        next: response => {
          const expected_end = dayjs(response.expected_end_date).tz('Europe/Madrid').format('HH:mm');
          this.snack.open(`Acceso de escritura a PostgreSQL habilitado hasta las ${expected_end}h.`, 'Ok', {
            duration: 5000,
          });
        },
      });
    });
  }

  reload() {
    location.reload();
  }

  addWorkingTime() {
    const dialogRef = this.dialog.open(AddNewWorkingTimeDialogComponent, {
      width: '650px',
      data: {},
    });

    dialogRef
      .afterClosed()
      .toPromise()
      .then((data: WorkingTime) => {
        if (data != null) {
          this.api
            .createWorkingTime(data)
            .toPromise()
            .then(() => {
              this.fetchWorkingTimeSummary();
            })
            .catch((err: { error?: { error?: string } }) => {
              this.snack.open(err?.error?.error ?? 'Error al registrar el tiempo.', undefined, { duration: 6000 });
            });

          return;
        }
        this.fetchWorkingTimeSummary();
      });
  }

  openFormationDialog() {
    this.dialog.open(NewLearningResourceRequestDialogComponent, {
      width: '700px',
    });
  }

  openNewWindow() {
    this.platform.openNewWindow();
  }

  setWorkingTimeSummary(id: number) {
    this.setSummary = { ...this.workingTimeSummaryList[id] };
    this.currentSetShowing = id;
  }

  async copyLink(): Promise<void> {
    await this.utils.copyTextToClipboard(`${environment.angular_url}${this.router.url}`);

    this.snack.open('¡Enlace copiado correctamente!', 'Ok', { duration: 3000 });
  }

  openUpdateDialog() {
    const version = this.platform.newVersionAvailable();
    if (!version) {
      return;
    }

    this.dialog.open(UpdateAvailableDialogComponent, {
      width: '500px',
      data: { version },
      disableClose: true,
    });
  }

  private checkStartAttribute() {
    const start_attribute = this.activatedRoute.snapshot.queryParamMap.get('start');

    if (start_attribute != null) {
      this.router.navigate(this.activatedRoute.snapshot.url, {
        queryParams: {},
      });
    }
  }

  private listenCustomerSelected() {
    this.customerService.customerObservable.subscribe(data => {
      this.customerSelected = data;
    });
  }

  private listenControlSearch() {
    this.clientSelectControl.valueChanges.subscribe((search_string: string) => {
      if (search_string != null && search_string.length >= 3) {
        const params = {
          search: search_string,
        };
        this.api.getCompanies(params).subscribe(data => {
          this.available_customers = data.data;
        });
      } else this.available_customers = [];
    });
  }

  private handleClick(
    status: { status: string; flag?: string; absence?: boolean } & Partial<ClockOutScheduleWarningDialogData> &
      Partial<ClockInScheduleWarningDialogData>
  ) {
    if (status.status === 'error') {
      if (status.flag === 'no-register') {
        this.dialog.open(RegisterBeforeLeaveDialogComponent, { width: '500px' });
      } else if (status.flag === 'schedule-out-of-tolerance') {
        this.openLateExitDialog({
          scheduled_exit_time: status.scheduled_exit_time!,
          current_time: status.current_time!,
        });
      } else if (status.flag === 'schedule-entry-out-of-tolerance') {
        this.openEarlyEntryDialog({
          scheduled_entry_time: status.scheduled_entry_time!,
          current_time: status.current_time!,
        });
      } else if (status.flag === 'rest-open') {
        this.snack.open('No puedes registrar salida mientras tienes un descanso activo. Finaliza el descanso primero.', 'Ok', {
          duration: 5000,
        });
      } else if (status.flag === 'remote-work') {
        this.snack.open(
          'No puedes fichar desde fuera de la oficina sin teletrabajo permitido. Conéctate al WiFi de oficina o contacta con RRHH.',
          'Ok',
          { duration: 5000 }
        );
      } else if (status.flag === 'clock-busy') {
        this.snack.open('Ya hay un fichaje en curso, espera un momento.', 'Ok', { duration: 4000 });
      }
    }
  }

  private subscribeCurrentTask() {
    this.employee.currentTask.subscribe(
      (data: {
        current: { title: string; call_link?: string } | null;
        next: { title: string; start: Date } | null;
        showLiveIndicator: boolean;
      }) => {
        this.currentTaskTitle = data.current != null ? data.current.title : null;
        this.currentTaskCallLink = data.current?.call_link != null && data.current.call_link !== '' ? data.current.call_link : null;
        this.nextTaskTitle = data.next != null ? data.next.title : null;
        this.nextStartDate = data.next != null ? data.next.start : null;
        this.show_live_task_indicator = data.showLiveIndicator;
      }
    );
  }

  private listenClockScheduleStatus() {
    this.clock.statusObservable.subscribe((status: boolean): void => {
      this.clock_schedule_status = status;
    });
  }

  private listenClockRestStatus() {
    this.clock.realStatusObservable.subscribe(data => {
      if (data != null) {
        this.clock_rest_available = data.rest_available;
        this.clock_rest_total = data.rest_time_total_available_in_seconds;
        this.clock_rest_consumed = data.rest_time_consumed_in_seconds;
        this.rest_status_last_in = data.rest_status_last_in;
        this.rest_status_last_time = data.rest_status_last_time;
      }
    });
  }

  private _listenUser() {
    this.userService.currentUser.subscribe((data: User): void => {
      if (data == null) {
        return;
      }

      this.me = data;
      this.role_string =
        this.me?.current_working_contract?.current_working_contract_variable_condition?.department_categories
          ?.map(dc => dc.name)
          .join(', ') ??
        this.me?.current_partner_contract?.current_partner_contract_variable_condition?.departments?.map(d => d.name).join(', ') ??
        '';

      const user_has_changed = this.last_working_time_summary_user_id !== data.id;

      if (user_has_changed) {
        this.last_working_time_summary_user_id = data.id;
        this.currentSetShowing = 0;
        this.isAbsent.set(false);
        this.initIsAbsent();
        this.fetchWorkingTimeSummary();
      }
    });

    this.userService.currentParentUser.subscribe(data => {
      this.parentUser = data;
    });
  }

  cleanShareScreen() {
    if (
      confirm(
        '¿Estás seguro que quieres desactivar el modo reunión? Recuerda que si compartes pantalla debes tener este modo activado. Una vez lo desactives el programa no volverá a pedírtelo para este evento y tendrás que activarlo tú manualmente.'
      )
    ) {
      this.employee.stopMeetingMode();
    }
  }

  protected desimpersonar() {
    this.auth.desimpersonate();
    this.router.navigate(['/employees/direction/users']);
  }

  async fetchWorkingTimeSummary() {
    if (!this.permissions.isWorker()) {
      return;
    }

    // Usar isoWeek para que el domingo siga siendo la semana actual (L–D), no la siguiente.
    // day(7) en dayjs hace overflow al domingo de la semana siguiente cuando hoy es domingo.
    const monday = dayjs().startOf('isoWeek').toDate();
    const sunday = dayjs().endOf('isoWeek').toDate();

    this.api
      .getWorkingTimeSummary(this.userService.getCurrentUser().id.toString(), monday, sunday)
      .subscribe((data: WorkingTimeSummary) => {
        if (data != undefined) {
          // Solo se hace la primera vez
          if (this.setSummary === undefined) {
            this.currentSetShowing = 0;
          }
          this.workingTimeSummaryList = [];

          // Calcular horas de hoy
          const todayHours = this.calculateTodayHours(data);

          // Vaciamos la lista y la llenamos con los datos actualizados
          this.workingTimeSummaryList.push(
            {
              name: 'Semana',
              done: data.done_hours ? data.done_hours * 3600 : 0,
              expected: data.total_hours ? data.total_hours * 3600 : 0,
              companyProductId: null,
              logoUrl: null,
            },
            {
              name: 'Hoy',
              done: todayHours.done,
              expected: todayHours.expected,
              companyProductId: null,
              logoUrl: null,
            }
          );

          // Process company_products from children
          if (data.children) {
            Object.keys(data.children).forEach(key => {
              const child = data.children![key];
              if (key.includes('CompanyProduct_') && child.company_product_id) {
                this.workingTimeSummaryList.push({
                  name: child.company_product_title || 'Producto',
                  done: child.done_hours ? child.done_hours * 3600 : 0,
                  expected: (child.expected_hours || 0) * 3600,
                  companyProductId: child.company_product_id,
                  logoUrl: null,
                });
              }
            });
          }

          // Se muestra las horas previamente seleccionadas o, por defecto, las semanales
          this.setWorkingTimeSummary(this.currentSetShowing);

          this.initWorkingTimeSummaryAutomaticToggle();

          // Check working hours and extra hours after summary is loaded
          this.checkWorkingHoursAndExtraHours();
        }
      });
  }

  private calculateTodayHours(data: WorkingTimeSummary): { done: number; expected: number } {
    const today = dayjs().startOf('day').toDate();

    // Buscar el día de hoy en el array de days
    const todayData = data.days.find(day => dayjs(day.day).isSame(today, 'day'));

    if (todayData) {
      return {
        done: todayData.done_hours * 3600, // Convertir horas a segundos
        expected: todayData.total_hours * 3600, // Convertir horas a segundos
      };
    }

    // Si no se encuentra el día de hoy, devolver 0
    return {
      done: 0,
      expected: 0,
    };
  }

  private async initWorkingTimeSummaryAutomaticToggle() {
    while (true) {
      await this.utils.sleep(1 * 60 * 1000);

      if (!this.workingTimeSummaryList || this.workingTimeSummaryList.length === 0) {
        continue;
      }

      // Aumentamos el índice
      this.currentSetShowing++;

      // Volver al principio si llegamos al final
      if (this.currentSetShowing >= this.workingTimeSummaryList.length) {
        this.currentSetShowing = 0;
      }

      // Mostramos el siguiente resumen
      this.setWorkingTimeSummary(this.currentSetShowing);
    }
  }

  private initIsAbsent() {
    const from = dayjs().startOf('d').toDate();
    const to = dayjs().endOf('d').toDate();

    this.api.getMyAbsences(from, to).subscribe(absences => {
      if (absences.holidays.length > 0 || absences.employee_calendar_registers.filter(ecr => ecr.all_day).length > 0) {
        this.isAbsent.set(true);
      }
    });
  }

  private checkWorkingHoursAndExtraHours() {
    // Get today's expected hours from the working time summary
    const todayHours = this.workingTimeSummaryList.find(item => item.name === 'Hoy');
    this.hasWorkingHoursToday.set(todayHours ? todayHours.expected > 0 : false);

    const user = this.userService.getCurrentUser();
    const today = dayjs().format('YYYY-MM-DD');

    this.api.getRemoteWorkStatus().subscribe((response: RemoteWorkStatus): void => {
      this.isAtOffice.set(response.at_office);
      this.can_work_remotely.set(response.can_work_remotely);
    });

    // Check for approved extra hours (general)
    this.api
      .getWorkingExtraHoursPeriods({
        user_id: user.id,
        status: EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED,
      })
      .subscribe((response: ArrayResponse<WorkingExtraHoursPeriod>): void => {
        const periods = response.data.filter((period: WorkingExtraHoursPeriod): boolean => {
          const start = dayjs(period.start_date);
          const end = dayjs(period.end_date);
          const todayDate = dayjs(today);
          return todayDate.isSameOrAfter(start, 'day') && todayDate.isSameOrBefore(end, 'day');
        });

        this.hasApprovedExtraHours.set(periods.length > 0);
      });
  }
}

interface WorkingTimeSummaryItem {
  name: string;
  done: number;
  expected: number;
  companyProductId: number | null;
  logoUrl: string | null;
}
